import { Component } from '@theme/component';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a product siblings picker.
 * Displays as a dropdown on desktop and a fullscreen panel on mobile.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} trigger – The trigger button.
 * @property {HTMLElement} panel – The panel containing the product list.
 *
 * @extends Component<Refs>
 */
class ProductSiblingsPicker extends Component {
  requiredRefs = ['trigger', 'panel'];

  #isOpen = false;
  #isExpanded = false;

  /**
   * Toggles the picker open/closed.
   */
  toggle = () => {
    if (this.#isOpen) {
      this.close();
    } else {
      this.#open();
    }
  };

  /**
   * Opens the picker.
   */
  #open() {
    this.#isOpen = true;
    this.setAttribute('open', '');
    this.refs.trigger.setAttribute('aria-expanded', 'true');

    if (isMobileBreakpoint()) {
      document.body.style.overflow = 'hidden';
    }

    requestAnimationFrame(() => {
      document.addEventListener('click', this.#handleDocumentClick);
      document.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the picker.
   */
  close = () => {
    if (!this.#isOpen) return;

    this.#isOpen = false;
    this.removeAttribute('open');
    this.refs.trigger.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';

    document.removeEventListener('click', this.#handleDocumentClick);
    document.removeEventListener('keydown', this.#handleKeyDown);
  };

  /**
   * Handles clicks outside the picker to close it.
   *
   * @param {MouseEvent} event
   */
  #handleDocumentClick = (event) => {
    if (!this.contains(/** @type {Node} */ (event.target))) {
      this.close();
    }
  };

  /**
   * Handles Escape key to close the picker.
   *
   * @param {KeyboardEvent} event
   */
  #handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      this.refs.trigger.focus();
    }
  };

  /**
   * Toggles the show more/less state for overflow items.
   */
  toggleShowMore = () => {
    this.#isExpanded = !this.#isExpanded;

    if (this.#isExpanded) {
      this.setAttribute('data-expanded', '');
    } else {
      this.removeAttribute('data-expanded');
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this.#handleDocumentClick);
    document.removeEventListener('keydown', this.#handleKeyDown);
    document.body.style.overflow = '';
  }
}

if (!customElements.get('product-siblings-picker')) {
  customElements.define('product-siblings-picker', ProductSiblingsPicker);
}
